import React from 'react';
import {
    BlankModal,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    VStack,
    DataList,
    DataItem,
} from '@cad-arc';
import { useApiClient } from '~/api-client';
import {
    ActionTypeEnum,
    QueueViewsResource,
    UnitQueueWireDto,
    UnitTimerResource,
} from '@mark43/cad-api';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { useForm } from 'react-hook-form';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';

export type UnitTimersResetToComponentType = typeof UnitTimersResetToModal;

type ResetTimerToForm = {
    timerMinutes: string;
};

export const UnitTimersResetToModal: React.FC<{ unitId: number }> = ({ unitId }) => {
    const apiClient = useApiClient();

    const form = useForm<ResetTimerToForm>({
        defaultValues: {
            timerMinutes: '',
        },
        mode: 'onBlur',
    });

    const modalControls = useFullscreenModalControls();

    const query = useQuery(['unit-queue-view', unitId], () => {
        return apiClient
            .request<QueueViewsResource.GetUnitQueueViewsByIds>({
                method: 'POST',
                url: 'queue_views/unit_queue_views/ids/',
                data: {
                    unitIds: [unitId],
                    isPinToPrimaryActive: false,
                    agencyTypeGlobalAttrIds: [],
                    assignmentStatus: undefined,
                    dispatchAreaIds: [],
                    unitTypeAttrIds: [],
                    queueColumnFilters: [],
                    isCadUnitLevelDispositionsEnabled: false,
                },
            })
            .then((response) => {
                return response.data.data?.[0];
            });
    });
    const unitQueueView = query.data;

    const resetTimerToMutation = useMutation(
        (props: { unitQueueView: UnitQueueWireDto; value: string }) => {
            return apiClient.request<UnitTimerResource.Update2>({
                method: 'POST',
                url: 'unit_timer/',
                data: {
                    actionType: ActionTypeEnum.RESET_UNIT_TIMER_TO.name,
                    unitId: props.unitQueueView.id,
                    agencyEventId: props.unitQueueView.eventQueueView?.id,
                    plusMinutes: parseInt(props.value, 10),
                },
            });
        },
        {
            onSuccess() {
                modalControls.close();
            },
        }
    );

    const onSubmit = React.useCallback(
        (props: ResetTimerToForm): void => {
            if (unitQueueView) {
                resetTimerToMutation.mutate({
                    value: props.timerMinutes,
                    unitQueueView,
                });
            }
        },
        [resetTimerToMutation, unitQueueView]
    );

    return (
        <IntegratedFormProvider
            query={query}
            mutation={resetTimerToMutation}
            onSubmit={onSubmit}
            form={form}
        >
            <BlankModal isOpen={true} onClose={() => modalControls.close()}>
                <ModalHeader>Reset Timer To...</ModalHeader>
                <ModalBody>
                    <FormBody>
                        {unitQueueView ? (
                            <VStack spacing={4} align="left" paddingTop={5} paddingBottom={5}>
                                <DataList direction="row" variant="inline">
                                    <DataItem dataKey="Unit">{unitQueueView.callSign}</DataItem>
                                    <DataItem dataKey="Status">{unitQueueView.status}</DataItem>
                                    <DataItem dataKey="Default Timer">
                                        {`${unitQueueView.timerDefaultLength}M`}
                                    </DataItem>
                                </DataList>
                                <FormNumberInput
                                    autoFocus={true}
                                    label="Reset Timer"
                                    control={form.control}
                                    name="timerMinutes"
                                    rules={{
                                        required: 'Required',
                                    }}
                                    numberInputProps={{
                                        width: '120px',
                                        rightAddon: 'M',
                                        min: 0,
                                        max: 999999,
                                        precision: 0,
                                    }}
                                />
                            </VStack>
                        ) : (
                            <div></div>
                        )}
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton
                            onClick={() => {
                                modalControls.close();
                            }}
                        />
                        <FormSaveButton>Reset Timer</FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
